import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { parseApiError } from "../../services/apiError";
import PeoplePicker from "./PeoplePicker";
import { sharePayload } from "./sharePayload";

/**
 * Share something with specific people (household sharing D2). Saving sets
 * it to "Specific people" with exactly this list; Cancel leaves it as it
 * was. `description` says what the people added may do with it — edit (a
 * drawing) or read and copy (a snippet).
 */
function ShareDialog({
  open,
  title,
  description,
  sharedWith,
  idPrefix,
  onClose,
  onSave,
}) {
  const [people, setPeople] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [fieldError, setFieldError] = useState(null);

  useEffect(() => {
    if (open) {
      setPeople(
        (sharedWith || []).map((p) => ({
          id: p.id,
          username: p.username,
        })),
      );
      setError(null);
      setFieldError(null);
    }
  }, [open, sharedWith]);

  const save = async () => {
    setSaving(true);
    setError(null);
    setFieldError(null);
    try {
      await onSave({ visibility: "people", ...sharePayload(people) });
      onClose();
    } catch (err) {
      const { formError, fieldErrors } = parseApiError(
        err,
        "Couldn't share it. Try again.",
      );
      const field = []
        .concat(fieldErrors.shared_with_ids ?? [])
        .concat(fieldErrors.share_with ?? []);
      setFieldError(field.length ? field.join(" ") : null);
      setError(field.length ? null : formError);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !saving && onClose()}>
      <DialogContent
        showCloseButton={!saving}
        className="p-0"
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => saving && event.preventDefault()}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          document.getElementById(`${idPrefix}-share-who`)?.focus();
        }}
      >
        <DialogHeader className="border-b px-6 py-4 text-left">
          <DialogTitle className="font-display text-xl">
            Share “{title}”
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 px-6 py-4" data-slot="dialog-body">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <PeoplePicker
            idPrefix={idPrefix}
            people={people}
            onChange={setPeople}
            canEdit
            serverError={fieldError}
          />
        </div>
        <DialogFooter className="border-t px-6 py-4">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving && <Loader2 className="animate-spin" />}
            Share
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

ShareDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  sharedWith: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number, username: PropTypes.string }),
  ),
  idPrefix: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default ShareDialog;
